/**
 * 経費管理表(月次台帳)の集計。
 *
 * 事業部 → 階層(拠点…) → 科目 のツリーに全出金を畳む。
 * - 固定/変動は取引の設定(cost_type)に従う。切り替えていない取引は従来どおり
 *   source で判定(recurring=固定 / それ以外=変動)し、科目行のバッジで表す
 * - 実績と見込みを列で分ける。振り分けは plEntryOf()(plRecognition.ts)だけで行い、状態を直接並べない
 *   (2026-10-22 発注者決定: 経費は承認時点で計上。月は計上日の月)。型の名前 confirmed / pending はそれぞれ実績 / 見込み
 * - 階層未設定の取引は事業部直下の「(拠点未設定)」にまとめ、合計から漏らさない
 * - 資金移動(type='transfer')と収益・原価科目は対象外(出金=経費のみ)
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { PL_QUERY_STATUSES, isFixedCost } from '@core/lib/types';
import { plEntryOf } from '@core/pl/plRecognition';
import type {
  Account,
  DateStr,
  Department,
  HierarchyNode,
  MonthStr,
  Transaction,
  Yen,
} from '@core/lib/types';

/** 科目行(末端)。固定と変動は別の行に分ける */
export type LedgerAccountRow = {
  kind: 'account';
  key: string;
  accountId: string;
  accountName: string;
  isFixed: boolean;
  confirmed: Yen;
  pending: Yen;
  transactions: Transaction[];
};

export type LedgerNodeRow = {
  kind: 'node';
  key: string;
  label: string;
  levelLabel: string;
  confirmed: Yen;
  pending: Yen;
  children: LedgerRow[];
};

export type LedgerRow = LedgerNodeRow | LedgerAccountRow;

export type LedgerDepartment = {
  department: Department;
  confirmed: Yen;
  pending: Yen;
  rows: LedgerRow[];
};

export type LedgerData = {
  month: MonthStr;
  departments: LedgerDepartment[];
  totalConfirmed: Yen;
  totalPending: Yen;
  /** 科目の全社横断合計(地代家賃を拠点・事業部をまたいで合算) */
  byAccount: { accountId: string; accountName: string; confirmed: Yen; pending: Yen }[];
};

/** 対象月の取引(承認前+承認済み以降)をまとめて取得する。実績・見込みは buildLedger で plEntryOf() により分ける */
export async function fetchLedgerTransactions(month: MonthStr): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'transactions'),
      where('posting_month', '==', month),
      where('status', 'in', PL_QUERY_STATUSES),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Transaction);
}

const UNASSIGNED = '__unassigned__';

export function buildLedger(params: {
  month: MonthStr;
  transactions: Transaction[];
  departments: Department[];
  nodesByDepartment: Map<string, HierarchyNode[]>;
  accounts: Account[];
  /** 実績・見込みを判定する基準日。省略時は今日(JST) */
  today?: DateStr;
}): LedgerData {
  const { month, transactions, departments, nodesByDepartment, accounts, today } = params;
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  const nameOf = new Map(accounts.map((a) => [a.id, a.name]));

  // 出金=経費科目のみ。PL の対象外(差戻し・取消・資金移動)と、計上月が違うものは除く
  const isActual = new Map<string, boolean>();
  const targets = transactions.filter((t) => {
    if (lineOf.get(t.account_id) !== 'expense') return false;
    const e = plEntryOf(t, today);
    if (e.category === 'excluded' || e.month !== month) return false;
    isActual.set(t.id, e.category === 'actual');
    return true;
  });

  const byAccountTotal = new Map<string, { confirmed: Yen; pending: Yen }>();
  const result: LedgerDepartment[] = [];

  for (const department of departments) {
    const deptTx = targets.filter((t) => t.department_id === department.id);
    if (deptTx.length === 0) continue;

    const nodes = nodesByDepartment.get(department.id) ?? [];
    const byId = new Map(nodes.map((n) => [n.id, n]));

    // 取引を「トップレベルの階層ノード(拠点)」で束ねる。
    // path[0] がトップの祖先。階層未設定・不明ノードは UNASSIGNED へ
    const topOf = (t: Transaction): string => {
      if (!t.hierarchy_node_id) return UNASSIGNED;
      const node = byId.get(t.hierarchy_node_id);
      if (!node) return UNASSIGNED;
      return node.path.length > 0 ? (node.path[0] as string) : node.id;
    };

    const groups = new Map<string, Transaction[]>();
    for (const t of deptTx) {
      const key = topOf(t);
      const list = groups.get(key) ?? [];
      list.push(t);
      groups.set(key, list);
    }

    const buildAccountRows = (list: Transaction[], keyPrefix: string): LedgerAccountRow[] => {
      const map = new Map<string, LedgerAccountRow>();
      for (const t of list) {
        const isFixed = isFixedCost(t);
        const key = `${keyPrefix}:${t.account_id}:${isFixed ? 'f' : 'v'}`;
        let row = map.get(key);
        if (!row) {
          row = {
            kind: 'account',
            key,
            accountId: t.account_id,
            accountName: nameOf.get(t.account_id) ?? t.account_id,
            isFixed,
            confirmed: 0,
            pending: 0,
            transactions: [],
          };
          map.set(key, row);
        }
        if (isActual.get(t.id)) row.confirmed += t.amount;
        else row.pending += t.amount;
        row.transactions.push(t);

        const total = byAccountTotal.get(t.account_id) ?? { confirmed: 0, pending: 0 };
        if (isActual.get(t.id)) total.confirmed += t.amount;
        else total.pending += t.amount;
        byAccountTotal.set(t.account_id, total);
      }
      return [...map.values()].sort(
        (a, b) => Number(b.isFixed) - Number(a.isFixed) || a.accountName.localeCompare(b.accountName, 'ja'),
      );
    };

    const rows: LedgerRow[] = [];
    const topNodes = nodes.filter((n) => n.parent_id === null);
    for (const top of topNodes) {
      const list = groups.get(top.id);
      if (!list || list.length === 0) continue;
      const accountRows = buildAccountRows(list, `${department.id}:${top.id}`);
      rows.push({
        kind: 'node',
        key: `${department.id}:${top.id}`,
        label: top.name,
        levelLabel: top.level_label,
        confirmed: accountRows.reduce((s, r) => s + r.confirmed, 0),
        pending: accountRows.reduce((s, r) => s + r.pending, 0),
        children: accountRows,
      });
    }

    const unassigned = groups.get(UNASSIGNED);
    if (unassigned && unassigned.length > 0) {
      const accountRows = buildAccountRows(unassigned, `${department.id}:${UNASSIGNED}`);
      rows.push({
        kind: 'node',
        key: `${department.id}:${UNASSIGNED}`,
        label: '(拠点未設定)',
        levelLabel: '拠点',
        confirmed: accountRows.reduce((s, r) => s + r.confirmed, 0),
        pending: accountRows.reduce((s, r) => s + r.pending, 0),
        children: accountRows,
      });
    }

    result.push({
      department,
      confirmed: rows.reduce((s, r) => s + r.confirmed, 0),
      pending: rows.reduce((s, r) => s + r.pending, 0),
      rows,
    });
  }

  return {
    month,
    departments: result,
    totalConfirmed: result.reduce((s, d) => s + d.confirmed, 0),
    totalPending: result.reduce((s, d) => s + d.pending, 0),
    byAccount: [...byAccountTotal.entries()]
      .map(([accountId, v]) => ({
        accountId,
        accountName: nameOf.get(accountId) ?? accountId,
        ...v,
      }))
      .sort((a, b) => b.confirmed + b.pending - (a.confirmed + a.pending)),
  };
}

export type FixedFilter = 'all' | 'fixed' | 'variable';

/** 表示フィルタ(固定のみ/変動のみ/すべて)を適用したビューを作る */
export function applyFixedFilter(data: LedgerData, filter: FixedFilter): LedgerData {
  if (filter === 'all') return data;
  const wantFixed = filter === 'fixed';

  const departments = data.departments
    .map((dept) => {
      const rows = dept.rows
        .map((row) => {
          if (row.kind !== 'node') return row;
          const children = row.children.filter(
            (c) => c.kind === 'account' && c.isFixed === wantFixed,
          );
          return {
            ...row,
            children,
            confirmed: children.reduce((s, c) => s + c.confirmed, 0),
            pending: children.reduce((s, c) => s + c.pending, 0),
          };
        })
        .filter((row) => row.kind !== 'node' || row.children.length > 0);
      return {
        ...dept,
        rows,
        confirmed: rows.reduce((s, r) => s + r.confirmed, 0),
        pending: rows.reduce((s, r) => s + r.pending, 0),
      };
    })
    .filter((dept) => dept.rows.length > 0);

  return {
    ...data,
    departments,
    totalConfirmed: departments.reduce((s, d) => s + d.confirmed, 0),
    totalPending: departments.reduce((s, d) => s + d.pending, 0),
  };
}
